import React, { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';

import { formatSharePercent, sharePercent } from '../formatMetric';

/**
 * PLA-8815: the Rewards & Loyalty score dial. CONCENTRIC arcs, one per reading, around a single
 * composite figure, each a `groove` under a `progress` under a `cover`, and the wheel count tracks
 * the number of readings the tile lists beside it. `589:28265` holds TWO wheels (86, 62.28) for
 * `589:28264`; the three-wheel set (86, 73.15, 57.83) is `589:28293`, on `589:28283`. (An earlier
 * note here put all three on `589:28265`, which is a different dial.)
 *
 * NOT `DonutChart`, which divides ONE ring into segments of a whole: these arcs are independent
 * shares that do not sum to anything, so drawing them as segments would state a relationship the
 * card does not have. NOT `RadialPercent` either — that is one arc with the figure inside it.
 */
export interface IScoreRing {
  /** Level as a 0-1 fraction, the same scale as `RankedBarList.pct` and `LegendTable.pct`. */
  pct: number;
  /** A `tokens.color` value — the same colour the reading's own line carries. */
  color: string;
  /** Names the arc for assistive technology; the tile prints the reading itself beside the dial. */
  label: string;
}

export interface IScoreRingsProps {
  rings: IScoreRing[];
  /** Pre-formatted composite figure drawn at the centre — a score, not the sum of the arcs. */
  centerValue: string;
  className?: string;
}

/* Held as the outer diameter, a stroke and a CLEAR gap rather than as three diameters, so a tile
   with two readings and one with three both fall out of the same rule.

   PLA-8815, sized up from the file's 86. At 86 the three quantities do not all fit: the file's own
   3-wheel dial `589:28293` only reaches a 49.92px hole by drawing ~3.7px strokes, and one rule has
   to serve the 2-wheel dial too, which `589:28265` strokes at ~8. Holding 86 forced a choice between
   a hole big enough for the figure and a gap wide enough to read as separate arcs — the first pass
   at this bug took the hole and left 0.425px between the arcs, which is what "congested" meant.

   104 buys both: three 5px arcs with 5px of clear air between them still leave a 54px hole (74px on
   a two-reading tile). The tile is `min-height`, not a fixed height, so it grows with the dial; the
   cost is 18px of the text column beside it, measured at 1280 in both engines before choosing. */
const OUTER = 104;
const STROKE = 5;
const RING_GAP = 5;

/* RING_GAP is the air BETWEEN two arcs, so it is a radial step and the diameter falls by twice it.
   (Reading it as a diameter step is what closed the gaps to 0.425px.) */
const diameterAt = (index: number): number => OUTER - index * (STROKE + RING_GAP) * 2;

const radiusAt = (index: number): number => (diameterAt(index) - STROKE) / 2;

/* The hole is a CIRCLE, so a line centred in it fits its chord at that line's height, not the hole's
   full diameter. Derived from the two measurements rather than allowed for by a character count,
   which is what a Metabase-length figure would break. */
const fittedWidth = (holeRadius: number, lineHeight: number): number =>
  2 * Math.sqrt(Math.max(holeRadius ** 2 - (lineHeight / 2) ** 2, 0));

const Wrapper = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: ${OUTER}px;
  height: ${OUTER}px;
`;

/* -90deg puts 0% at twelve o'clock; the arcs run clockwise from there, as the file draws them. */
const Svg = styled.svg`
  display: block;
  transform: rotate(-90deg);
`;

const Groove = styled.circle`
  fill: none;
  stroke: ${tokens.color['track-bg']};
`;

const Progress = styled.circle`
  fill: none;
  stroke-linecap: round;
`;

const CenterValue = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: ${tokens.color['text-primary']};
`;

/* Split out of `CenterValue` so the figure can be MEASURED: `CenterValue` is a 100%-wide flex box,
   so its own box always reports the dial's width and never the ink's. `--dial-fit` is the shrink
   the layout effect derives from that measurement; `clamp` holds the token floor, so a payload long
   enough to need a big shrink stops at legible rather than disappearing. */
const Figure = styled.span`
  white-space: nowrap;
  font-size: clamp(
    ${tokens.type['score-dial-value-min-size']},
    calc(${tokens.type['score-dial-value-size']} * var(--dial-fit, 1)),
    ${tokens.type['score-dial-value-size']}
  );
  font-weight: ${tokens.type['score-dial-value-weight']};
  line-height: ${tokens.type['score-dial-value-line']};
`;

const ScoreRings: React.FC<IScoreRingsProps> = ({ rings, centerValue, className }) => {
  const figureRef = useRef<HTMLSpanElement>(null);
  const holeRadius = radiusAt(Math.max(rings.length - 1, 0)) - STROKE / 2;

  useLayoutEffect(() => {
    const figure = figureRef.current;

    if (!figure) {
      return undefined;
    }

    const fit = () => {
      // Reset first: the ink has to be read at the token size, or a second pass would compound the
      // shrink it applied on the first.
      figure.style.setProperty('--dial-fit', '1');

      const style = getComputedStyle(figure);
      const line = parseFloat(style.lineHeight) || parseFloat(style.fontSize);
      const ink = figure.getBoundingClientRect().width;
      const budget = fittedWidth(holeRadius, line);

      if (budget > 0 && ink > budget) {
        figure.style.setProperty('--dial-fit', String(budget / ink));
      }
    };

    fit();

    // Inter lands after first paint and is wider than the fallback stack, so a figure that fits when
    // measured against the fallback overflows the moment the webfont swaps in. `document.fonts` is
    // absent in jsdom, where there is no layout to measure anyway.
    let live = true;

    document.fonts?.ready.then(() => {
      if (live) {
        fit();
      }
    });

    return () => {
      live = false;
    };
  }, [centerValue, holeRadius]);

  return (
    <Wrapper className={className} data-testid="score-rings">
      <Svg width={OUTER} height={OUTER} viewBox={`0 0 ${OUTER} ${OUTER}`} aria-hidden="false">
        {rings.map((ring, index) => {
          const radius = radiusAt(index);
          const circumference = 2 * Math.PI * radius;
          // Clamped through the shared helper, so an arc and the line beside it cannot disagree
          // about what the same fraction reads as.
          const filled = (sharePercent(ring.pct) / 100) * circumference;

          return (
            <React.Fragment key={ring.label}>
              <Groove cx={OUTER / 2} cy={OUTER / 2} r={radius} strokeWidth={STROKE} />
              <Progress
                cx={OUTER / 2}
                cy={OUTER / 2}
                r={radius}
                strokeWidth={STROKE}
                stroke={ring.color}
                strokeDasharray={`${filled} ${circumference}`}
                data-testid="score-rings-arc"
              >
                <title>{`${ring.label}: ${formatSharePercent(ring.pct)}`}</title>
              </Progress>
            </React.Fragment>
          );
        })}
      </Svg>
      <CenterValue data-testid="score-rings-center">
        <Figure ref={figureRef} data-testid="score-rings-figure">
          {centerValue}
        </Figure>
      </CenterValue>
    </Wrapper>
  );
};

export default ScoreRings;
